'use client';

import { ReactNode } from 'react';
import { css, stack } from '@bts-web/utils-style-engine';
import { getAppConfig } from '@bts-web/core-app-config';
import { ResponsiveLayoutWithLogo } from '../../../layouts/ResponsiveLayoutWithLogo/ResponsiveLayoutWithLogo';
import { HiddenDesktop } from '../../../layouts/LayoutWithLeftDesktopNav/Reusable/HiddenDesktop/HiddenDesktop';
import { getResponsiveLayoutTranslations } from '../../../layouts/ResponsiveLayoutWithLogo/utils';
import { TFunction } from 'i18next';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { MobileLanguageDrawer } from './MobileLanguageDrawer';
import { HTMLRenderer } from '../../components/HTMLRenderer/HTMLRenderer';
import { DocumentationExternalLink } from '../../components';
import { getMainNavigationTranslations } from '../../../layouts/LayoutWithLeftDesktopNav/utils/getMainNavigationTranslations';
import { AppLogo } from '../../../layouts/LayoutWithLeftDesktopNav/Reusable/AppLogo/AppLogo.use-client';

const appConfig = getAppConfig();

export interface InfoLayoutProps extends React.PropsWithChildren {
  mainHeadingFirstLine: string;
  mainHeadingSecondLine?: string;
  mainHeadingSecondLineColor?: string;
  minorHeading?: string;
  description?: string;

  customContentSlot?: ReactNode;
  riskDisclosureLinkText?: string;
  riskDisclosureLinkUrl?: string;
  showLanguageSwitcher?: boolean;
  variant?: 'default' | 'primary' | 'dark' | 'light';
  isIslamicUser?: boolean;
}

const getBgForVariant = (variant: 'default' | 'primary' | 'dark' | 'light') => {
  switch (variant) {
    case 'primary':
      return 'screen_background.primary';

    case 'dark':
      return 'brand.fill_secondary';

    case 'light':
      return 'brand.fill_tertiary';

    case 'default':
      return appConfig?.appId === 4
        ? 'brand.fill_secondary'
        : 'neutrals.fill_quinary';
  }
};

export const InfoLayout = ({
  children,
  showLanguageSwitcher,
  mainHeadingFirstLine,
  mainHeadingSecondLine,
  mainHeadingSecondLineColor,
  minorHeading,
  description,
  customContentSlot,
  riskDisclosureLinkText,
  riskDisclosureLinkUrl,
  variant = 'default',
  isIslamicUser = false,
}: InfoLayoutProps) => {
  const { t, language } = useClientTranslation();

  const { supportedLanguages } = getAppConfig();

  const isLanguageSwitchShown =
    showLanguageSwitcher && supportedLanguages.length > 1;

  const translations = getResponsiveLayoutTranslations(t);

  const mainNavigationTranslations = getMainNavigationTranslations(
    t as TFunction,
  );

  return (
    <ResponsiveLayoutWithLogo
      translations={mainNavigationTranslations}
      showLanguageSwitcher={isLanguageSwitchShown}
      currentLanguage={language}
      isIslamicUser={isIslamicUser}
    >
      <div
        className={css({
          backgroundColor: getBgForVariant(variant),
          borderRadius: {
            base: 0,
            lg: 8,
          },
          display: 'flex',
          flexDirection: 'column',
          gap: 'small',
          height: '100%',
          width: '100%',
          px: 'medium',
          paddingTop: 'extra_large',
        })}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'medium',
          })}
        >
          <HiddenDesktop>
            {isLanguageSwitchShown ? (
              <div
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                })}
              >
                <AppLogo isIslamicUser={isIslamicUser} textLogoSize={160} />

                <MobileLanguageDrawer
                  translations={translations}
                  contrastVariant
                />
              </div>
            ) : (
              <AppLogo
                isIslamicUser={isIslamicUser}
                textLogoSize={160}
                color={
                  variant === 'dark'
                    ? 'neutrals.text_primary_inverted'
                    : 'neutrals.text_primary'
                }
              />
            )}
          </HiddenDesktop>

          <div
            data-testid="intro-header"
            aria-label="intro header"
            className={stack({ direction: 'column', gap: 'small' })}
          >
            <h1
              className={css({
                color:
                  variant === 'dark'
                    ? 'neutrals.text_primary_inverted'
                    : 'neutrals.text_primary',
                fontSize: {
                  base: 'headline.extra_large_semi_bold',
                  lg: 'display.large_bold',
                },
                fontWeight: {
                  base: 'headline.extra_large_semi_bold',
                  lg: 'display.large_bold',
                },
                lineHeight: {
                  base: 'headline.extra_large_semi_bold',
                  lg: 'display.large_bold',
                },
                letterSpacing: {
                  base: 'headline.extra_large_semi_bold',
                  lg: 'display.large_bold',
                },
              })}
            >
              <span>{mainHeadingFirstLine}</span>
              <br />
              {mainHeadingSecondLine && (
                <span
                  className={css({
                    ...(mainHeadingSecondLineColor
                      ? { color: mainHeadingSecondLineColor }
                      : {}),
                  })}
                >
                  {mainHeadingSecondLine}
                </span>
              )}
            </h1>

            {minorHeading && (
              <p
                className={css({
                  fontSize: 'label.large',
                  fontWeight: 'bold',
                  lineHeight: 'label.large',
                  letterSpacing: 'label.large',
                  color:
                    variant === 'dark'
                      ? 'neutrals.text_primary_inverted'
                      : 'neutrals.text_primary',
                })}
              >
                {minorHeading}
              </p>
            )}

            {description && (
              <div
                className={css({
                  color:
                    variant === 'dark'
                      ? 'neutrals.text_primary_inverted'
                      : 'neutrals.text_primary',
                  fontSize: {
                    base: 'label.large',
                  },
                  fontWeight: {
                    base: 'label.large',
                  },
                  letterSpacing: {
                    base: 'label.large',
                  },
                  lineHeight: {
                    base: 'label.large',
                  },
                  '& a': {
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                  },
                })}
              >
                <HTMLRenderer>{description}</HTMLRenderer>

                {riskDisclosureLinkText &&
                  riskDisclosureLinkUrl &&
                  language && (
                    <>
                      &nbsp;
                      <DocumentationExternalLink
                        size="small"
                        data-testid="risk-terms-link"
                        visual="primaryUnderline"
                        href={riskDisclosureLinkUrl}
                        label={riskDisclosureLinkText}
                      />
                      <span>.</span>
                    </>
                  )}
              </div>
            )}

            {customContentSlot}
          </div>
        </div>

        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          })}
        >
          {children}
        </div>
      </div>
    </ResponsiveLayoutWithLogo>
  );
};
